import React, { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  CircleCheckIcon,
  CloudUploadIcon,
  DownloadIcon,
  ImportIcon,
  InfoCircle,
  UploadIcon,
} from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { CloseModalButton } from '../../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../../components/sheet/index';
import { BackendRoute } from '../../../../../config/index';
import { QueryKey } from '../../../../../constants/index';
import { Stakeholder, useAppMutation, useLocale, useReactForm } from '../../../../../hooks/index';
import { Locale } from '../../../../../lang/index';
import { ApiService } from '../../../../../services/index';
import { PageRoute } from '../../../../../types/page.types';
import { StakeholderTypeGroup } from '../../../../../types/stakeholder.types';
import { ModifyGuideTab } from '../../../../modify-guide/modify-guide.types';
import StakeholderTypeGroupCombobox from '../stakeholder-type-combobox';
import { FormSchema, formSchema } from './bulk-modify.validation';

export type BulkModifyStakeholdersModalProps = {
  companyId: string;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  setOpen: (state: boolean) => void;
};

const BulkModifyStakeholdersModal: FC<BulkModifyStakeholdersModalProps> = ({
  companyId,
  invalidateQuery,
  isOpenModal,
  setOpen,
}) => {
  const { locale, messagesLocale } = useLocale();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    formState: { isValid, errors, dirtyFields },
  } = useReactForm({ schema: formSchema(messagesLocale) });

  const { downloadStakeholderInfo } = Stakeholder.useStakeholderInfo();

  const { mutate, isPending } = useAppMutation([QueryKey.STAKEHOLDERS_BULK_IMPORT, companyId], {
    mutationFn: async ({ file, typeGroup }: { file: File; typeGroup: StakeholderTypeGroup }) => {
      const fileData = new FormData();
      fileData.append('file', file);
      return ApiService.requestWithAuthResponse(
        `${BackendRoute.COMPANIES}/${companyId}/stakeholders/${typeGroup}/modify`,
        {
          method: 'POST',
          body: fileData,
          locale,
        },
      );
    },
    onSuccess: () => {
      setOpen(false);
      reset();
      invalidateQuery();
      toast.success(messagesLocale['STAKEHOLDERS_SUCCESSFULLY_IMPORTED']);
    },
  });

  const handleCloseModal = useCallback(() => {
    reset();
    setOpen(false);
  }, [reset, setOpen]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      mutate({
        file: data.file,
        typeGroup: data.stakeholderTypeGroup,
      });
    },
    [mutate],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setValue('file', acceptedFiles[0], { shouldValidate: true });
    },
    [setValue],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });

  const isValidFileState = getValues('file') && !getFieldState('file').invalid;
  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-fit border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden overflow-y-auto rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="BULK_MODIFY_STAKEHOLDERS" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <form className="flex h-full flex-col gap-4" onSubmit={handleSubmit(submitHandler)}>
            <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
              <StakeholderTypeGroupCombobox control={control} name="stakeholderTypeGroup" />
              <div className="flex h-11 w-full justify-between gap-4 text-nowrap">
                <Button
                  className="flex w-fit items-center gap-4 rounded border border-gray-100 px-3 py-[6px] shadow-xs"
                  disabled={!dirtyFields.stakeholderTypeGroup}
                  onClick={() =>
                    downloadStakeholderInfo({
                      companyId,
                      typeGroup: getValues('stakeholderTypeGroup'),
                    })
                  }
                  styleType="NONE"
                  type="button"
                >
                  <DownloadIcon
                    iconColor={!dirtyFields.stakeholderTypeGroup ? '#D0D5DD' : '#344054'}
                  />
                  <p
                    className={twMerge(
                      'text-sm font-[450] text-gray-700',
                      !dirtyFields.stakeholderTypeGroup && 'text-gray-300',
                    )}
                  >
                    <AppFormattedMessage id="DOWNLOAD_STAKEHOLDER_INFO_PART1" />{' '}
                    <span
                      className={twMerge(
                        'max-xs:hidden',
                        !dirtyFields.stakeholderTypeGroup && 'text-gray-300',
                      )}
                    >
                      <AppFormattedMessage id="DOWNLOAD_STAKEHOLDER_INFO_PART2" />
                    </span>{' '}
                    <AppFormattedMessage id="DOWNLOAD_STAKEHOLDER_INFO_PART3" />
                  </p>
                </Button>

                <Link
                  className="flex w-fit cursor-pointer items-center justify-center gap-2 rounded text-sm font-[450] text-brand-700 underline disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-300 disabled:ring-1 disabled:ring-gray-200"
                  rel="noopener noreferrer"
                  target="_blank"
                  to={`${PageRoute.GUIDES}/${ModifyGuideTab.MODIFY_STAKEHOLDERS}`}
                >
                  <p className="text-sm font-[450] text-brand-700 underline">
                    {locale === Locale.FR ? (
                      <AppFormattedMessage id="VIEW_MODIFICATION_GUID" />
                    ) : (
                      <>
                        <AppFormattedMessage id="VIEW" />{' '}
                        <span className="max-[520px]:hidden">modification</span> guide
                      </>
                    )}
                  </p>
                  <ImportIcon />
                </Link>
              </div>
              <input {...getInputProps()} className="hidden" type="file" />
              <label
                {...getRootProps()}
                className="relative flex w-full cursor-pointer flex-col items-center gap-6 rounded-lg border border-dashed border-gray-200 py-6"
              >
                {isValidFileState ? (
                  <CircleCheckIcon />
                ) : (
                  <>
                    {errors.file ? (
                      <InfoCircle className="size-14" thinIconVariant />
                    ) : (
                      <CloudUploadIcon />
                    )}
                  </>
                )}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex justify-center">
                    {isValidFileState ? (
                      <span className="text-sm font-[550] text-forest-500">
                        <AppFormattedMessage id="FILE_PROCESSED_SUCCESSFULLY" />
                      </span>
                    ) : errors.file ? (
                      <span className="text-sm font-[550] text-fireside-600">
                        {errors.file.message?.toString()}
                      </span>
                    ) : (
                      <p className="text-sm font-medium text-gray-700">
                        <span className="underline">
                          <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                        </span>
                        <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 text-[10px] font-[450] leading-4 text-gray-500">
                    {isValidFileState ? (
                      <>
                        <span>{(getValues('file') as unknown as File).name}</span>
                        <span>
                          {((getValues('file') as unknown as File).size / 1024).toFixed(3)}
                          <AppFormattedMessage id="KILOBYTE" />
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          <AppFormattedMessage
                            id="SUPPORTED_FORMATS"
                            values={{
                              formats: 'xlsx',
                            }}
                          />
                        </span>
                        <span>
                          <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
                        </span>
                      </>
                    )}
                  </div>
                  {errors.file && (
                    <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                      <UploadIcon />
                      <AppFormattedMessage id="UPLOAD_AGAIN" />
                    </span>
                  )}
                </div>
              </label>
              {isValidFileState && !getValues('stakeholderTypeGroup') && (
                <span className="flex items-center gap-1 text-xs font-[450] text-fireside-600">
                  <InfoCircle />
                  Please select Stakeholder Type from the dropdown before saving upload
                </span>
              )}
            </div>

            <div className="flex h-9 w-full justify-end gap-3 px-6">
              <Button
                className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700"
                onClick={() => setOpen(false)}
                styleType="NONE"
                type="button"
              >
                <AppFormattedMessage id="CLOSE" />
              </Button>
              <Button
                className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
                disabled={!isValid || isPending}
                isLoading={isPending}
                type="submit"
              >
                <AppFormattedMessage id="SAVE" />
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default BulkModifyStakeholdersModal;
